import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { useTypewriterEffect } from "../../hooks/useTypewriterEffect";
import { CITATION_MARKER, remarkCitations } from "../../lib/markdown/remarkCitations";
import type { Citation, EvArtifact } from "../../types/agent-chat";
import { TypingAnimation } from "../chat/TypingAnimation";
import { ArtifactCard } from "./ArtifactCard";
import { markdownComponents } from "./markdownComponents";
import { SourcesPanel } from "./SourcesPanel";
import type { AgentMessageState } from "./agentReducer";
import { ToolStep } from "./ToolStep";

export interface AgentMessageProps {
  message: AgentMessageState;
  /** Every citation seen this conversation, keyed by short id. */
  citations: Record<string, Citation>;
  onOpenDocument?: (citation: Citation) => void;
  /** Open a generated document in the side panel. */
  onOpenArtifact?: (artifact: EvArtifact) => void;
  /** Tool call id of the artifact currently shown in the side panel. */
  activeArtifactId?: string | null;
}

/**
 * One turn in the agent conversation.
 *
 * Assistant turns show their tool steps above the answer, so a reader can see
 * what the model looked at before it wrote, inline citation chips inside the
 * answer, a card per generated document (the document itself lives in the
 * side panel), and a sources panel below it.
 */
export function AgentMessage({
  message,
  citations,
  onOpenDocument,
  onOpenArtifact,
  activeArtifactId,
}: AgentMessageProps) {
  if (message.role === "user") {
    return (
      <div className="mb-8 flex justify-end">
        <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-brand-primary px-4 py-2.5 text-left text-sm text-white shadow-sm">
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10 text-left">
      {message.steps.length > 0 && (
        <div className="mb-5">
          {message.steps.map((step) => (
            <ToolStep key={step.toolCallId} step={step} />
          ))}
        </div>
      )}

      {message.text === "" && message.isStreaming ? (
        <TypingAnimation />
      ) : (
        <AssistantBody
          message={message}
          citations={citations}
          onOpenDocument={onOpenDocument}
        />
      )}

      {message.artifacts.map((artifact) => (
        <ArtifactCard
          key={artifact.tool_call_id}
          artifact={artifact}
          isActive={artifact.tool_call_id === activeArtifactId}
          onOpen={(a) => onOpenArtifact?.(a)}
        />
      ))}

      <SourcesPanel
        citations={citations}
        citedIds={message.citedIds}
        unusedIds={message.unusedIds}
        onOpenDocument={onOpenDocument}
      />

      {message.model && !message.isStreaming && (
        <p
          className="mt-1 text-right text-[10px] tracking-wide text-brand-muted/60"
          title={`Modèle utilisé pour cette réponse : ${message.model}`}
        >
          {shortModelName(message.model)}
        </p>
      )}
    </div>
  );
}

/** "ovhcloud/gpt-oss-120b" reads as "gpt-oss-120b"; the full id stays in the tooltip. */
function shortModelName(model: string): string {
  const last = model.split("/").pop() ?? model;
  return last.length > 40 ? `${last.slice(0, 39)}…` : last;
}

function AssistantBody({
  message,
  citations,
  onOpenDocument,
}: {
  message: AgentMessageState;
  citations: Record<string, Citation>;
  onOpenDocument?: (citation: Citation) => void;
}) {
  // Historical turns are already complete, so they render at once; a live turn
  // reveals progressively.
  // atomicPattern keeps a marker from being revealed one character at a time,
  // which would flash a half-written "[[cite:S" before it resolves to a chip.
  const { displayedText } = useTypewriterEffect(message.text, {
    speed: 15,
    startImmediately: !message.isHistorical,
    atomicPattern: CITATION_MARKER,
  });
  const text = message.isHistorical ? message.text : displayedText;

  return (
    <div className="prose prose-sm max-w-none text-brand-text prose-strong:text-brand-text prose-p:my-3 prose-p:leading-relaxed prose-headings:mt-6 prose-headings:mb-3 prose-li:my-1 prose-table:my-5 prose-th:py-2.5 prose-th:px-3 prose-td:py-3 prose-td:px-3">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkCitations]}
        components={markdownComponents(citations, onOpenDocument)}
      >
        {text}
      </ReactMarkdown>
      {message.isStreaming && <span className="ml-0.5 animate-pulse">▋</span>}
    </div>
  );
}
